import React, { useState, useEffect } from 'react';
import Modal from './Modal';

// `outcome` is fixed by the caller ('upheld' or 'changed'), not chosen here - the caller
// already knows which one applies based on which action the reviewer took (e.g. clicking
// "No Change Needed" vs finishing a "Change Assessment" edit), so there's nothing to get
// wrong by picking the other outcome without actually having made a change.
const ResolveDisputeModal = ({ open, onClose, onSave, outcome, disputeReason }) => {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  // Reset fields whenever the modal opens
  useEffect(() => {
    if (open) {
      setReason('');
      setError('');
    }
  }, [open]);

  const handleSubmit = async () => {
    const trimmed = reason.trim();
    if (!trimmed) {
      setError('Please explain your decision.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await onSave(trimmed);
    } catch (err) {
      setError(err?.message || 'Failed to resolve dispute. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const title = outcome === 'changed' ? 'Finish Resolving Dispute — Assessment Changed' : 'Resolve Dispute — No Change Needed';
  const description = outcome === 'changed'
    ? 'You’ve saved a change to this call’s assessment. Explain what changed and why.'
    : 'Explain why the original assessment is correct and is being left as-is.';

  return (
    <Modal open={open} onClose={onClose} title={title}>
      <div className="space-y-4">
        {disputeReason && (
          <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
            <p className="text-xs font-medium text-gray-500 mb-1">Agent's dispute reason</p>
            <p className="text-sm text-gray-700">{disputeReason}</p>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Reason
          </label>
          <p className="text-xs text-gray-500 mb-2">{description}</p>
          <textarea
            rows={5}
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="Explain your decision..."
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
          />
        </div>

        {error && (
          <p className="text-sm text-red-600">{error}</p>
        )}

        <div className="flex justify-end space-x-3 pt-2">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving || !reason.trim()}
            className="px-4 py-2 text-sm font-medium text-white bg-cyan-600 rounded-md hover:bg-cyan-700 disabled:opacity-50 flex items-center"
          >
            {saving && (
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            )}
            {saving ? 'Resolving...' : 'Mark Reviewed'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ResolveDisputeModal;
